/**
 * Where the buttons were: what the room is doing about its columns, in words,
 * and the one thing a person might want to do — undo the last change.
 *
 * Nothing here starts work. Naming the columns is the cartographer's call when
 * enough phrases are in; fitting phrases to them runs during a dig and when it
 * ends. See `columnChange.ts`.
 */
import { useState, type ReactNode } from 'react';
import { changeWords, type ColumnChange } from './columnChange';

const WHO: Record<string, string> = {
  skeptic: 'the fact checker checked it',
  human: 'you',
  cartographer: 'the sorter',
};

export default function ColumnsStatus({
  unfitted,
  hasSheet,
  change,
  onUndo,
}: {
  /** Phrases today's columns have not been put to yet. */
  unfitted: number;
  hasSheet: boolean;
  change: ColumnChange | null;
  onUndo?: (frameId: number) => Promise<void> | void;
}) {
  const [busy, setBusy] = useState(false);
  const undo = async () => {
    if (!change || !onUndo || busy) return;
    setBusy(true);
    try {
      await onUndo(change.frameId);
    } finally {
      setBusy(false);
    }
  };
  const parts: ReactNode[] = [];
  if (change) {
    parts.push(
      <span key="change" data-testid="column-change">
        {change.isUndo ? 'Columns put back' : 'Columns updated'}: {changeWords(change)}
        {!change.isUndo && change.by && WHO[change.by] ? ` (${WHO[change.by]})` : ''}
        {change.canUndo && !change.isUndo && onUndo && (
          <>
            {' · '}
            <button
              type="button"
              onClick={undo}
              disabled={busy}
              className="underline hover:no-underline disabled:opacity-50"
              data-testid="undo-columns"
            >
              {busy ? 'undoing…' : 'Undo'}
            </button>
          </>
        )}
      </span>,
    );
  }
  if (unfitted > 0) {
    parts.push(
      <span key="pile" data-testid="columns-pile">
        {hasSheet
          ? `${unfitted} phrase${unfitted === 1 ? '' : 's'} not yet read against the columns - the room fits them as it goes`
          : `${unfitted} phrase${unfitted === 1 ? '' : 's'} recorded - the sorter names the columns once there are enough`}
      </span>,
    );
  }
  if (!parts.length) return null;
  return (
    <div className="text-xs text-gray-500 dark:text-gray-400 flex flex-wrap gap-x-3" data-testid="columns-status">
      {parts}
    </div>
  );
}
